import { Injectable } from "@angular/core";
import {
	CoursePage,
	CoursePageContent,
	CourseVersionContributor,
	CourseVersionGlossaryTerm,
	CourseVersionNavigationTree,
	CourseVersionReference,
	PostResponse
} from "@data/course";
import { Observable } from "rxjs";
import { ServiceResponse } from "@data/response";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { ResponseHandler } from "@service/system/responseHandler.service";

const API_URL = API.API_URL;

@Injectable({
	providedIn: "root"
})
export class CourseEditorService {
	private _coursesURL = `${API_URL}/course`;

	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	getCoursePagesByGUID(cvItemID: string): Observable<ServiceResponse<CoursePage[]>> {
		return this.responseHandler.httpGet<CoursePage[]>(`${this._coursesURL}/${cvItemID}/navigationtree/page/find`);
	}

	getCourseVersionContributors(
		cvItemID: string,
		active: boolean = true
	): Observable<ServiceResponse<CourseVersionContributor[]>> {
		return this.responseHandler.httpGet<CourseVersionContributor[]>(
			`${this._coursesURL}/${cvItemID}/contributor/find`,
			{ active: active }
		);
	}

	setCourseVersionContributor(
		cvItemID: string,
		body: CourseVersionContributor
	): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpPost<PostResponse>(`${this._coursesURL}/${cvItemID}/contributor/set`, body);
	}

	getCourseVersionGlossaryTerm(
		cvItemID: string,
		active: boolean = true
	): Observable<ServiceResponse<CourseVersionGlossaryTerm[]>> {
		return this.responseHandler.httpGet<CourseVersionGlossaryTerm[]>(
			`${this._coursesURL}/${cvItemID}/glossary/find`,
			{ active: active }
		);
	}

	setCourseVersionGlossaryTerm(
		cvItemID: string,
		termID: number,
		body: CourseVersionGlossaryTerm
	): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpPost<PostResponse>(`${this._coursesURL}/${cvItemID}/glossary/set`, body);
	}

	getCourseVersionNavigationTree(cvItemID: string): Observable<ServiceResponse<CourseVersionNavigationTree[]>> {
		return this.responseHandler.httpGet<CourseVersionNavigationTree[]>(`${this._coursesURL}/${cvItemID}/tree`);
	}

	getCourseVersionReferences(
		cvItemID: string,
		active: boolean = true
	): Observable<ServiceResponse<CourseVersionReference[]>> {
		return this.responseHandler.httpGet<CourseVersionReference[]>(
			`${this._coursesURL}/${cvItemID}/references/find`,
			{ active: active }
		);
	}

	setCourseVersionReferences(
		cvItemID: string,
		body: CourseVersionReference
	): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpPost<PostResponse>(`${this._coursesURL}/${cvItemID}/reference/set`, body);
	}

	getPageContentByID(cvItemID: string, pageID: string): Observable<ServiceResponse<CoursePageContent[]>> {
		return this.responseHandler.httpGet<CoursePageContent[]>(
			`${this._coursesURL}/${cvItemID}/navigationtree/page/${pageID}/content/find`
		);
	}

	getContentPath(prodKey: string): Observable<string> {
		return this.httpManager.httpGet<any>(`${this._coursesURL}/path`, { cvItemID: prodKey });
	}

	setPageContent(cvItemID: string, body: string): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpPost<PostResponse>(
			`${this._coursesURL}/${cvItemID}/navigationtree/page/set`,
			body
		);
	}

	deletePageContent(cvItemID: string, pageId: string): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpDelete<PostResponse>(
			`${this._coursesURL}/${cvItemID}/navigationtree/page/${pageId}/content/delete`,
			{
				pageId: pageId
			}
		);
	}

	deletePage(cvItemID: string, pageId: string): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpDelete<PostResponse>(
			`${this._coursesURL}/${cvItemID}/navigationtree/page/${pageId}/page/delete`,
			{
				pageId: pageId
			}
		);
	}

	setCourseVersionPages(cvItemID: string, body: any): Observable<ServiceResponse<PostResponse>> {
		return this.responseHandler.httpPost<PostResponse>(`${this._coursesURL}/${cvItemID}/navigationtree/set`, body);
	}

	uploadContributorImage(cvItemID: string, body: any): Observable<any> {
		return this.httpManager.httpPostMediaFile<any>(
			`${this._coursesURL}/${cvItemID}/contributor/uploadContributorImage`,
			body
		);
	}
}
